import { Spinner, Text } from "@hackthenorth/north";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "north.js";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { Header, PageWrapper } from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import { useUserContext } from "src/shared/contexts";
import { useGetHackerQuery } from "src/shared/contexts/HackerContext/graphql/getHacker.generated";
import { useGetMeQuery } from "src/shared/contexts/UserContext/graphql/getMe.generated";
import { Subtitle } from "src/shared/styles";
import { mediaQueries } from "src/theme/deviceBreakpoints";

import ProfileCard from "../components/cards/ProfileCard";
import QuestCard from "../components/cards/QuestCard";
import LearnMoreModal from "../components/modals/hacker/home/learn_more";
import WelcomeBackModal from "../components/modals/hacker/home/welcome_back";
import { useGooseGamesContext } from "../context";
import { useGetAllGooseGameHackerQuestsQuery } from "../graphql/hacker/getAllGooseGameQuests.generated";
import { useMyGooseGamePlayerQuery } from "../graphql/hacker/myGooseGamePlayer.generated";
import { getPalNumberFromFields } from "../hackPals";
import { sortQuestsByVisibility } from "../questUtils";
import { GooseToQuestTypeMap, Quest, QuestLog } from "../types";

const GooseGamesHome: React.FC = () => {
  const { isOrganizer } = useUserContext();
  const {
    data: gooseGameQuestData,
    loading: fetchingQuests,
    refetch: refetchGooseGameQuestData,
  } = useGetAllGooseGameHackerQuestsQuery({
    variables: { eventId: EVENT_ID },
  });
  const { data: myProfileData } = useGetMeQuery();
  const { data: myGGPlayerData, refetch: refetchMyGooseGamePlayer } =
    useMyGooseGamePlayerQuery();
  const { data: getHackerData } = useGetHackerQuery({
    variables: { myId: myProfileData ? myProfileData.me.id : -1 },
    skip: !myProfileData,
  });

  const {
    showWelcomeBack,
    myQuestLogData,
    refetchQuestLogs,
    handleWelcomeBackClose,
    showLearnMore,
    handleLearnMoreOpen,
    handleLearnMoreClose,
  } = useGooseGamesContext();

  const myProfile = myProfileData?.me;

  const palNumber = getPalNumberFromFields(getHackerData?.claims?.[0]?.fields);

  const [gooseGameQuests, setGooseGameQuests] = useState<Quest[]>([]);

  const getQuestCount = (logs?: QuestLog[]) => {
    const freqMap: Record<number, number> = {};

    if (logs) {
      for (const log of logs) {
        if (freqMap[log.quest_id] === undefined) {
          freqMap[log.quest_id] = 1;
        } else {
          freqMap[log.quest_id]++;
        }
      }
    }

    return freqMap;
  };

  const refetchQuestData = () => {
    refetchGooseGameQuestData();
    refetchQuestLogs();
    refetchMyGooseGamePlayer();
  };

  const questCount = useMemo(
    () => getQuestCount(myQuestLogData?.myGooseGameQuestLogs),
    [myQuestLogData]
  );

  const completedCount = gooseGameQuests.filter(
    (quest) => (questCount[quest.id] || 0) >= quest.completion_limit
  ).length;

  useEffect(() => {
    refetchMyGooseGamePlayer();
  }, [completedCount, refetchMyGooseGamePlayer]);

  useEffect(() => {
    if (gooseGameQuestData && gooseGameQuestData.gooseGameQuests) {
      const tempQuests: Quest[] = [];
      const tempCompletedQuests: Quest[] = [];

      for (const quest of gooseGameQuestData.gooseGameQuests) {
        const mappedQuest: Quest = {
          ...quest,
          type: GooseToQuestTypeMap[quest.type],
          description: quest.description ?? undefined,
          quest_event_activity_category:
            quest.quest_event_activity_category ?? undefined,
          question_string: quest.question_string ?? undefined,
          question_answer: undefined,
        };

        if (questCount[quest.id] >= quest.completion_limit) {
          tempCompletedQuests.push(mappedQuest);
        } else {
          tempQuests.push(mappedQuest);
        }
      }
      setGooseGameQuests(
        sortQuestsByVisibility(
          [...tempQuests, ...tempCompletedQuests],
          isOrganizer,
          (quest) => questCount[quest.id] >= quest.completion_limit
        )
      );
    }
  }, [gooseGameQuestData, isOrganizer, questCount]);

  return (
    <>
      <Header
        title="Goose Games"
        subtitle="Complete quests to collect points and redeem exclusive prizes!"
        actionButton={
          <StyledButton
            trailingIcon={<InformationCircleIcon width={18} />}
            size="md"
            color="tertiary"
            onClick={handleLearnMoreOpen}
          >
            Learn more
          </StyledButton>
        }
      />
      <StyledPageWrapper>
        <ProfileCard
          profile={myProfile}
          palNumber={palNumber}
          ggPlayer={
            myGGPlayerData ? myGGPlayerData.myGooseGamePlayer : undefined
          }
        />
        <HeaderFlex>
          <TitleText mods="heading h3">Quests</TitleText>
          <SubtitleText>
            {completedCount}/{gooseGameQuests.length} completed
          </SubtitleText>
        </HeaderFlex>
        <QuestCardGrid>
          {fetchingQuests ? (
            <Spinner />
          ) : (
            gooseGameQuests.map((quest) => (
              <QuestCard
                key={`Quest ${quest.id}`}
                quest={quest}
                view="player"
                progress={questCount[quest.id]}
                refetchQuests={refetchQuestData}
              />
            ))
          )}
        </QuestCardGrid>
      </StyledPageWrapper>

      {!showLearnMore && showWelcomeBack && (
        <WelcomeBackModal
          isOpen={showWelcomeBack}
          onClose={handleWelcomeBackClose}
        />
      )}
      {showLearnMore && (
        <LearnMoreModal isOpen={showLearnMore} onClose={handleLearnMoreClose} />
      )}
    </>
  );
};

const TitleText = styled(Text)`
  color: #374151 !important;
`;

const SubtitleText = styled(Subtitle)`
  color: ${({ theme }) => theme.globalConstants.color.textTertiary} !important;
`;

const StyledPageWrapper = styled(PageWrapper)`
  background: ${({ theme }) => theme.globalConstants.color.backgroundLight};

  ${mediaQueries.largeMobile} {
    background: ${({ theme }) => theme.globalConstants.color.white};
  }
`;

const HeaderFlex = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
  color: ${({ theme }) => theme.globalConstants.color.textPrimary} !important;
  padding: 0 !important;
`;

const QuestCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  padding-bottom: 24px;

  ${mediaQueries.medium} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${mediaQueries.largeMobile} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

export default GooseGamesHome;
